import { getInAppBrowserName } from '@/utils/inAppBrowser';

const STORAGE_KEY = 'tifosi_traffic_source_v1';

export interface TrafficSourceSnapshot {
  channel: string;
  referrer: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  inAppBrowser: string;
  landingPath: string;
}

function readSearchParams(): URLSearchParams {
  if (typeof window === 'undefined') return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

function inferChannel(referrer: string, utmSource: string, inApp: string | null): string {
  const utm = utmSource.toLowerCase();
  if (utm.includes('snap')) return 'snapchat';
  if (utm.includes('instagram') || utm === 'ig') return 'instagram';
  if (utm.includes('facebook') || utm === 'fb') return 'facebook';
  if (utm.includes('tiktok')) return 'tiktok';
  if (inApp) return inApp.toLowerCase();

  const ref = referrer.toLowerCase();
  if (!ref) return 'direct';
  if (ref.includes('snapchat')) return 'snapchat';
  if (ref.includes('instagram')) return 'instagram';
  if (ref.includes('facebook') || ref.includes('fb.com')) return 'facebook';
  if (ref.includes('tiktok')) return 'tiktok';
  if (ref.includes('google.')) return 'google';
  if (ref.includes('tifosi-foot.com')) return 'tifosi_site';
  return 'referral';
}

/** Capture la source d’entrée une fois par session (UTM, referrer, navigateur in-app). */
export function captureTrafficSource(): TrafficSourceSnapshot {
  if (typeof window === 'undefined') {
    return {
      channel: 'unknown',
      referrer: '',
      utmSource: '',
      utmMedium: '',
      utmCampaign: '',
      inAppBrowser: '',
      landingPath: '',
    };
  }

  try {
    const existing = sessionStorage.getItem(STORAGE_KEY);
    if (existing) {
      return JSON.parse(existing) as TrafficSourceSnapshot;
    }
  } catch {
    /* ignore */
  }

  const params = readSearchParams();
  const inApp = getInAppBrowserName();
  const referrer = (document.referrer || '').trim();
  const utmSource = (params.get('utm_source') || '').trim();
  const utmMedium = (params.get('utm_medium') || '').trim();
  const utmCampaign = (params.get('utm_campaign') || '').trim();
  const snapshot: TrafficSourceSnapshot = {
    channel: inferChannel(referrer, utmSource, inApp),
    referrer: referrer.slice(0, 500),
    utmSource,
    utmMedium,
    utmCampaign,
    inAppBrowser: inApp || '',
    landingPath: `${window.location.pathname}${window.location.search}`.slice(0, 300),
  };

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* ignore */
  }

  return snapshot;
}

/** Libellé compact pour le champ GoPaaS `source` (Texte). */
export function formatTrafficSourceForEvent(snapshot?: TrafficSourceSnapshot): string {
  const s = snapshot ?? captureTrafficSource();
  const parts = [s.channel];
  if (s.inAppBrowser) parts.push(`inapp:${s.inAppBrowser}`);
  if (s.utmSource) parts.push(`utm:${s.utmSource}`);
  if (s.utmMedium) parts.push(s.utmMedium);
  if (s.utmCampaign) parts.push(s.utmCampaign);
  return parts.join('|').slice(0, 240);
}
